import React, {useState, useEffect, useCallback} from 'react';
import {onlineProcessesService} from "../../services";
import QuadralBigNumber from '@quadral/quadral-component-common/dist/components/bigNumber'
import QuadralContainer from '@quadral/quadral-component-common/dist/components/container'
import QuadralList from '@quadral/quadral-component-common/dist/components/list';
import QuadralTypography from '@quadral/quadral-component-common/dist/components/typography';
import QuadralButton from '@quadral/quadral-component-common/dist/components/button';
import QuadralModal from '@quadral/quadral-component-common/dist/components/modal';
import QuadralAutocomplete from '@quadral/quadral-component-common/dist/components/autocomplete';
import QuadralTextField from '@quadral/quadral-component-common/dist/components/textField';
import QuadralBadge from "@quadral/quadral-component-common/dist/components/badge"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faCheck, faXmark, faFileInvoice } from '@fortawesome/free-solid-svg-icons';

const IncomeAndExpenses = ({participationId, participation, references, updateParticipation, roles, isN3, ...props}) => {
    const [rowsRFR, setRowsRFR] = useState([])
    const [rowsCharges, setRowsCharges] = useState([])
    const [personOptions, setPersonOptions] = useState([])
    const [allParticipantsHaveRfr, setAllParticipantsHaveRfr] = useState(false)
    const [participantsWithRfr, setParticipantsWithRfr] = useState([])
    const [addRfrModalOpen, setAddRfrModalOpen] = useState(false)
    const [newRfrForm, setNewRfrForm] = useState({
        personRecordId: '',
        taxAmount: ''
    })

    const columnsRFR = [
        {
            id: "name",
            label: "Personne",
            sortable: true,
            typography: "tableBodyWide"
        },
        {
            id: "type",
            label: "Type",
            sortable: true,
        },
        {
            id: "value",
            label: "Montant",
            sortable: true,
            typography: "tableBodyWide"
        },
        {
            id: "status",
            label: "Justificatif",
            sortable: true,
        },
        // {
        //     id: "action",
        //     label: "Actions",
        // },
    ];

    const columnsCharges = [
        {
            id: "person",
            label: "Personne",
            sortable: true,
            typography: "tableBodyWide"
        },
        {
            id: "type",
            label: "Type",
            sortable: true,
        },
        {
            id: "nature",
            label: "Nature",
            sortable: true,
        },
        {
            id: "value",
            label: "Montant",
            sortable: true,
            typography: "tableBodyWide"
        },
        {
            id: "status",
            label: "Justificatif",
            sortable: true,
        },
        // {
        //     id: "action",
        //     label: "Actions",
        // },
    ];

    function getStatusIcon(status){
        switch (status) {
            case 'valide' :
                return <FontAwesomeIcon icon={faCheck}/>
            case 'a_fournir' :
                return <FontAwesomeIcon icon={faFileInvoice}/>
            case 'a_valider' :
                return <FontAwesomeIcon icon={faFileInvoice}/>
            default :
                return <FontAwesomeIcon icon={faXmark}/>
        };
    };

    const splitData = useCallback(async (data) => {
        try {
            const personMap = {};

            for (const item of data) {
                if (item?.personId && !personMap[item.personId]) {
                    try {
                        const personRecord = await onlineProcessesService.getPersonRecordById(
                            participationId,
                            item.personId
                        );

                        personMap[item.personId] = `${personRecord?.firstname} ${personRecord?.lastname}`;
                    } catch (error) {
                        console.error(`Erreur lors de la récupération de la personne ${item.personId}:`, error);
                        personMap[item.personId] = item?.name || '-';
                    }
                }
            }

            const getTypeLabel = (typeCode) => {
                const typeRef = references?.INCOME_AND_EXPENSE_TYPE?.find(
                    ref => ref.code === typeCode
                );
                return typeRef?.label || typeCode;
            };
            
            const getNatureLabel = (natureCode) => {
                const natureRef = references?.INCOME_AND_EXPENSE_NATURE_REVENU?.find(
                    ref => ref.code === natureCode
                );
                return natureRef?.label || natureCode;
            };

            const getStatusLabel = (statusCode) => {
                const statusRef = references?.INCOME_AND_EXPENSE_STATUS?.find(
                    ref => ref.code === statusCode
                );
                return statusRef?.label || statusCode;
            }

            const rfrData = data
                .filter(item => item?.type === 'RFR')
                .map(item => ({
                    ...item,
                    value: item.taxAmount ? `${item?.taxAmount} €` : <QuadralTypography sx={{wordBreak: 'break-word', textTransform:'none'}}variant='dataLabel'>Non renseigné</QuadralTypography>,
                    name: (personMap[item?.personId] || item?.name),
                    type: getTypeLabel(item?.type) || <QuadralTypography sx={{wordBreak: 'break-word', textTransform:'none'}}variant='dataLabel'>Non renseigné</QuadralTypography>,
                    status:
                        <QuadralBadge
                            icon={getStatusIcon(item?.status)}
                            label={getStatusLabel(item?.status) || 'Non justifié'}
                            color={item?.status === 'valide' ? 'success'
                                : item?.status === 'refusee' ? 'error'
                                : item?.status === 'a_valider' ? 'info'
                                : item?.status === 'a_fournir' ? 'info'
                                : 'warning'
                        }/>
                }));

            const chargesData = data
                .filter(item => item?.type === 'prestations_sociales' || item?.type === 'revenu')
                .map(item => ({
                    ...item,
                    value: item.amount ? `${item?.amount} €` : <QuadralTypography sx={{wordBreak: 'break-word', textTransform:'none'}}variant='dataLabel'>Non renseigné</QuadralTypography>,
                    person: personMap[item?.personId] || item?.name,
                    type: getTypeLabel(item?.type) || <QuadralTypography sx={{wordBreak: 'break-word', textTransform:'none'}}variant='dataLabel'>Non renseigné</QuadralTypography>,
                    nature: getNatureLabel(item?.nature) || 'Prestation',
                    status: 
                        <QuadralBadge
                            icon={getStatusIcon(item?.status)}
                            label={getStatusLabel(item?.status) || 'Non justifié'}
                            color={item?.status === 'valide' ? 'success'
                                : item?.status === 'refusee' ? 'error'
                                : item?.status === 'a_valider' ? 'info'
                                : item?.status === 'a_fournir' ? 'info'
                                : 'warning'
                        }/>
                }));

            setRowsRFR(rfrData);
            setRowsCharges(chargesData);
        } catch (error) {
            console.error('Erreur lors du traitement des données:', error);
        }
    }, [participationId, references]);

    const loadIncomeAndExpenses = useCallback(() => {
        if (props.personIds && props.personIds.length > 0 && participation) {
            const promises = props.personIds.map(personId =>
                onlineProcessesService.getIncomeAndCharges(participationId, personId)
            )
            Promise.all(promises)
                .then(dataReceived => {
                    const hasRfrByPersonId = new Set();

                    dataReceived.forEach((personEntries, index) => {
                        const personId = props.personIds[index];
                        const hasRfr = (personEntries || []).some(item => item?.type === 'RFR');

                        if (hasRfr) {
                            hasRfrByPersonId.add(personId);
                        }
                    });

                    setAllParticipantsHaveRfr(
                        props.personIds.every(personId => hasRfrByPersonId.has(personId))
                    );
                    setParticipantsWithRfr(Array.from(hasRfrByPersonId));

                    const mergedData = dataReceived.flat();
                    splitData(mergedData)
                })
                .catch(err => {
                    console.error(err);
                    setAllParticipantsHaveRfr(false);
                    setParticipantsWithRfr([]);
                });
        } else {
            setAllParticipantsHaveRfr(false);
            setParticipantsWithRfr([]);
        }
    }, [props.personIds, participation, participationId, splitData])

    useEffect(() => {
        loadIncomeAndExpenses();
    }, [loadIncomeAndExpenses])

    useEffect(() => {
        if (!props.personIds || props.personIds.length === 0 || !participationId) {
            setPersonOptions([]);
            return;
        }

        const eligiblePersonIds = props.personIds.filter(
            personId => !participantsWithRfr.includes(personId)
        );

        if (eligiblePersonIds.length === 0) {
            setPersonOptions([]);
            return;
        }

        Promise.all(
            eligiblePersonIds.map(async (personId) => {
                try {
                    const person = await onlineProcessesService.getPersonRecordById(participationId, personId);
                    return {
                        code: personId,
                        label: `${person?.firstname || ''} ${person?.lastname || ''}`.trim() || String(personId)
                    };
                } catch (error) {
                    console.error(`Erreur lors de la récupération de la personne ${personId}:`, error);
                    return {
                        code: personId,
                        label: String(personId)
                    };
                }
            })
        ).then(setPersonOptions);
    }, [props.personIds, participationId, participantsWithRfr]);

    function resetNewRfrForm() {
        setNewRfrForm({
            personRecordId: '',
            taxAmount: ''
        });
    }

    function openAddRfrModal() {
        resetNewRfrForm();
        setAddRfrModalOpen(true);
    }

    function closeAddRfrModal() {
        setAddRfrModalOpen(false);
        resetNewRfrForm();
    }

    async function createRfr() {
        const personRecordId = newRfrForm.personRecordId;
        const year = new Date().getFullYear() - 2;
        const taxAmount = Number(newRfrForm.taxAmount);

        if (!personRecordId || Number.isNaN(taxAmount)) {
            props.quadralToast?.error('Veuillez renseigner la personne et le montant.');
            return;
        }

        if (taxAmount < 0) {
            props.quadralToast?.error('Le montant doit être positif.');
            return;
        }

        const newRFR = {
            type: 'RFR',
            taxAmount,
            status: 'a_fournir',
            nature: 'Revenu_fiscal_de_reference',
            personId: personRecordId,
            surveyParticipationId: participationId,
            year
        };

        try {
            props.showLoader?.();
            await onlineProcessesService.createIncomeAndCharges({ participationId, personRecordId }, newRFR);

            // Create the matching supporting document category once the RFR is created.
            const rfrSupportingDocument = {
                surveyParticipationId: participationId,
                personId: personRecordId,
                category: 'RFR',
                status: 'a_fournir'
            };

            try {
                const existingSupportingDocuments = await onlineProcessesService.getSupportingDocuments(
                    participationId,
                    personRecordId
                );

                const hasExistingRfrSupportingDocument = (existingSupportingDocuments || []).some(
                    (doc) => doc?.category === 'RFR'
                );

                if (!hasExistingRfrSupportingDocument) {
                    const response = await onlineProcessesService.createSupportingDocument(
                        { participationId, personRecordId },
                        rfrSupportingDocument
                    );

                    const supportingDocumentId = response?.salesforceId;
                    if (!supportingDocumentId) {
                        throw new Error('ID de pièce justificative manquant dans la réponse');
                    }

                    props.updateData?.();
                }
            } catch (error) {
                console.error('Erreur lors de la création de la pièce justificative RFR :', error);
                props.quadralToast?.error('Erreur lors de la création de la pièce justificative RFR');
            }

            props.quadralToast?.success('RFR ajouté avec succès');
            closeAddRfrModal();
            loadIncomeAndExpenses();
            updateParticipation();
        } catch (error) {
            props.quadralToast?.error(error.response?.data?.message || 'Erreur lors de la création du RFR');
        } finally {
            props.hideLoader?.();
        }
    }

    return(<>
        <QuadralModal
            size='L'
            open={addRfrModalOpen}
            setOpen={setAddRfrModalOpen}
            title='Ajouter un RFR'
            noButton
            action={<>
                <QuadralButton color='primary' onClick={closeAddRfrModal}>
                    Annuler
                </QuadralButton>
                <QuadralButton color='secondary' startIcon={<FontAwesomeIcon icon={faPlus}/>} onClick={createRfr}>
                    Ajouter
                </QuadralButton>
            </>}
        >
            <QuadralContainer orientation='column' sx={{gap: '1rem'}}>
                <QuadralTypography variant='smallCardTitle'>A quel membre du foyer est rattaché le RFR à ajouter ?</QuadralTypography>
                <QuadralAutocomplete
                    id='personRecordId'
                    label='Membre du foyer'
                    noPadding
                    options={personOptions}
                    value={personOptions.find(p => p.code === newRfrForm.personRecordId) || null}
                    getOptionLabel={(option) => option?.label || ''}
                    onChange={(e, value) => {
                        setNewRfrForm(prev => ({ ...prev, personRecordId: value?.code || '' }));
                    }}
                />
                <QuadralTypography variant='smallCardTitle'>Quel est le montant du RFR à ajouter ?</QuadralTypography>
                <QuadralTextField
                    id='taxAmount'
                    name='taxAmount'
                    label='Montant'
                    noPadding
                    value={newRfrForm.taxAmount}
                    onChange={(value) => {
                        setNewRfrForm(prev => ({ ...prev, taxAmount: value }));
                    }}
                />
            </QuadralContainer>
        </QuadralModal>
        <QuadralContainer orientation='column' sx={{gap: '2rem'}}>
            <QuadralContainer orientation='row' sx={{gap: '2rem'}}>
                {/* 
                
                TODO Rétablir en V2.0
                
                <QuadralBigNumber
                    title='Total Revenu(s) Mensuel(s)'
                    value='ADD_VALUE_HERE'
                />
                
                <QuadralBigNumber
                    title='Total Prestations'
                    value='ADD_VALUE_HERE'
                /> */}
                <QuadralBigNumber
                    title='Revenu Total Foyer'
                    value={
                        participation?.totalRevenue != null
                        ? `${participation.totalRevenue.toLocaleString('fr-FR')} €`
                        : 'Non renseigné'
                    }
                    color='primary'
                />
                <QuadralBigNumber
                    title='RFR Total'
                    value={
                        participation?.totalRFR != null
                        ? `${participation.totalRFR.toLocaleString('fr-FR')} €`
                        : 'Non renseigné'
                    }
                    color='primary'
                />
            </QuadralContainer>
            <QuadralContainer orientation='column' sx={{alignItems: 'flex-end', gap: '0.5rem'}}>
                {!isN3 && (<>
                    <QuadralButton
                        label='Ajouter un RFR (année N-2)'
                        color='secondary'
                        startIcon={<FontAwesomeIcon icon={faPlus}/>}
                        disabled={!props.personIds || props.personIds.length === 0 || allParticipantsHaveRfr}
                        onClick={openAddRfrModal}
                    />
                    {allParticipantsHaveRfr && (
                        <QuadralTypography variant='dataLabel' sx={{wordBreak: 'break-word', textTransform: 'none'}}>
                            Tous les participants ont déjà un RFR (année N-2).
                        </QuadralTypography>
                    )}
                </>)}
            </QuadralContainer>
            <QuadralContainer>
                <QuadralList
                    title='Revenu fiscal de référence'
                    columns={columnsRFR}
                    rows={rowsRFR}
                    sortable
                />
                {rowsRFR.length === 0 && (
                    <QuadralContainer sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '2rem'}}>
                        <QuadralTypography sx={{wordBreak: 'break-word', textTransform:'none'}}variant='dataLabel'>
                            Non renseigné
                        </QuadralTypography>
                    </QuadralContainer>
                )}
            </QuadralContainer>
            <QuadralContainer>
                <QuadralList
                    title='Revenus et prestations du foyer'
                    columns={columnsCharges}
                    rows={rowsCharges}
                    sortable
                />
                {rowsCharges.length === 0 && (
                    <QuadralContainer sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '2rem'}}>
                        <QuadralTypography sx={{wordBreak: 'break-word', textTransform:'none'}}variant='dataLabel'>
                            Non renseigné
                        </QuadralTypography>
                    </QuadralContainer>
                )}
            </QuadralContainer>
        </QuadralContainer>
    </>)
}

export default IncomeAndExpenses;